import React, { useState, useEffect, useRef, Fragment } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { supabase } from '../../lib/supabaseClient';
import CandidateUpdateRequestsPanel from './CandidateUpdateRequestsPanel';
import {
  MessageSquare,
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock,
  Send,
  User,
  ArrowLeft,
  Search as SearchIcon,
  MessagesSquare
} from 'lucide-react';
import { cn } from '../../lib/utils';

// Simple relative time formatter
const formatRelativeTime = (date) => {
  const now = new Date();
  const past = new Date(date);
  const diffInSeconds = Math.floor((now - past) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)} weeks ago`;
  return `${Math.floor(diffInSeconds / 2592000)} months ago`;
};

// Format full timestamp
const formatTimestamp = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Generate candidate display ID (same as other views)
const generateCandidateDisplayId = (uuid) => {
  if (!uuid) return 'XXXX';
  let hash = 0;
  for (let i = 0; i < uuid.length; i++) {
    const char = uuid.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  const displayId = Math.abs(hash).toString(36).toUpperCase().substring(0, 4).padStart(4, '0');
  return displayId;
};

const CandidateRequests = () => {
  const [requests, setRequests] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [mobileSelectedRequest, setMobileSelectedRequest] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});

  // Message sending state
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  // Ref for auto-scrolling to bottom
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    fetchRequests();

    // Set up realtime subscription for candidate_requests
    const requestsChannel = supabase
      .channel('recruiter-requests-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'candidate_requests'
        },
        (payload) => {
          console.log('Request update received:', payload);
          fetchRequests();
        }
      )
      .subscribe();

    // Set up realtime subscription for request_messages
    const messagesChannel = supabase
      .channel('recruiter-messages-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'request_messages'
        },
        (payload) => {
          console.log('Message update received:', payload);
          // Refresh messages if we have a selected request
          if (selectedRequest) {
            fetchMessages(selectedRequest.id);
          }
        }
      )
      .subscribe();

    // Cleanup subscriptions on unmount
    return () => {
      supabase.removeChannel(requestsChannel);
      supabase.removeChannel(messagesChannel);
    };
  }, [selectedRequest]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('candidate_requests')
        .select(`
          *,
          candidate:candidates(id, first_name, last_name, location_city, location_state, clearance_level, notice_period),
          client:clients(id, name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Calculate unread counts for all requests in parallel (much faster)
      const unreadCountsMap = {};

      if (data && data.length > 0) {
        const countPromises = data.map(async (request) => {
          const query = supabase
            .from('request_messages')
            .select('*', { count: 'exact', head: true })
            .eq('request_id', request.id)
            .eq('sender_type', 'client');

          // If there's a last read timestamp, only count messages after that
          if (request.last_read_by_recruiter_at) {
            query.gt('created_at', request.last_read_by_recruiter_at);
          }

          const { count } = await query;
          return { id: request.id, count: count || 0 };
        });

        const results = await Promise.all(countPromises);
        results.forEach(result => {
          unreadCountsMap[result.id] = result.count;
        });
      }

      setRequests(data || []);
      setUnreadCounts(unreadCountsMap);
      setError(null);
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (requestId) => {
    try {
      setMessagesLoading(true);
      console.log('Fetching messages for request:', requestId);

      const { data, error } = await supabase
        .from('request_messages')
        .select('*')
        .eq('request_id', requestId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Supabase error fetching messages:', error);
        throw error;
      }

      console.log('Messages fetched:', data?.length || 0, 'messages');
      setMessages(data || []);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setMessages([]); // Ensure messages is empty array on error
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleSelectRequest = async (request) => {
    console.log('Recruiter selecting request:', request.id);
    setSelectedRequest(request);
    setMobileSelectedRequest(request);
    setNewMessage('');
    setSendError(null);
    fetchMessages(request.id);

    // Mark as read by recruiter
    try {
      const timestamp = new Date().toISOString();
      console.log('Marking request as read at:', timestamp);

      const { error: updateError } = await supabase
        .from('candidate_requests')
        .update({ last_read_by_recruiter_at: timestamp })
        .eq('id', request.id);

      if (updateError) {
        console.error('Error updating last_read_by_recruiter_at:', updateError);
        throw updateError;
      }

      console.log('Successfully marked as read, updating local state');
      // Update unread count to 0 for this request
      setUnreadCounts(prev => ({
        ...prev,
        [request.id]: 0
      }));
    } catch (error) {
      console.error('Error marking request as read:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) {
      setSendError('Please enter a message');
      return;
    }

    setSending(true);
    setSendError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('Not authenticated');
      }

      const { error: insertError } = await supabase
        .from('request_messages')
        .insert({
          request_id: selectedRequest.id,
          sender_type: 'recruiter',
          sender_user_id: user.id,
          sender_name: 'Appsytec Recruiter',
          sender_email: user.email,
          message: newMessage.trim()
        });

      if (insertError) throw insertError;

      // Clear message input
      setNewMessage('');

      // Refresh messages (will happen automatically via realtime, but good for immediate feedback)
      fetchMessages(selectedRequest.id);

    } catch (err) {
      console.error('Error sending message:', err);
      setSendError(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getUnreadBadge = (requestId) => {
    const count = unreadCounts[requestId] || 0;
    if (count > 0) {
      return (
        <Badge variant="destructive" className="gap-1">
          Unread
        </Badge>
      );
    }
    return null;
  };

  const filteredRequests = requests.filter((request) => {
    const searchLower = search.toLowerCase();
    const candidateId = generateCandidateDisplayId(request.candidate?.id);
    const clientName = request.client?.name?.toLowerCase() || '';
    const message = request.message?.toLowerCase() || '';

    return candidateId.toLowerCase().includes(searchLower) ||
      clientName.includes(searchLower) ||
      message.includes(searchLower);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Tabs defaultValue="client-requests" className="space-y-4">
      <TabsList>
        <TabsTrigger value="client-requests">Client Requests</TabsTrigger>
        <TabsTrigger value="candidate-requests">Candidate Update Requests</TabsTrigger>
      </TabsList>

      <TabsContent value="candidate-requests">
        <CandidateUpdateRequestsPanel />
      </TabsContent>

      <TabsContent value="client-requests" className="space-y-4">
      {/* Error State */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Chat-style Layout */}
      <section className="flex h-[calc(100vh-12rem)] gap-6">
        {/* Left Sidebar - Request List */}
        <div className="flex w-full flex-col gap-2 sm:w-64 lg:w-80 2xl:w-96">
          <div className="bg-background sticky top-0 z-10 -mx-4 px-4 pb-3 shadow-md sm:static sm:z-auto sm:mx-0 sm:p-0 sm:shadow-none">
            <div className="flex items-center justify-between py-2">
              <div className="flex gap-2">
                <h1 className="text-2xl font-bold">Messages</h1>
                <MessagesSquare size={20} />
              </div>
              <Badge variant="secondary" className="text-base px-3 py-1">
                {Object.values(unreadCounts).filter(count => count > 0).length} Unread
              </Badge>
            </div>

            <label
              className={cn(
                'focus-within:ring-ring focus-within:ring-1 focus-within:outline-hidden',
                'border-border flex h-10 w-full items-center space-x-0 rounded-md border ps-2'
              )}
            >
              <SearchIcon size={15} className="me-2 stroke-slate-500" />
              <span className="sr-only">Search</span>
              <input
                type="text"
                className="w-full flex-1 bg-inherit text-sm focus-visible:outline-hidden"
                placeholder="Search messages..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
          </div>

          <ScrollArea className="-mx-3 h-full overflow-scroll p-3">
            {filteredRequests.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-lg font-medium">No messages found</p>
                <p className="text-sm text-muted-foreground mt-2">
                  {search ? 'Try a different search term' : 'Client messages will appear here'}
                </p>
              </div>
            ) : (
              filteredRequests.map((request) => {
                const candidateId = generateCandidateDisplayId(request.candidate?.id);
                const previewText = request.message?.substring(0, 50) + '...';

                return (
                  <Fragment key={request.id}>
                    <button
                      type="button"
                      className={cn(
                        'group hover:bg-accent hover:text-accent-foreground',
                        'flex w-full rounded-md px-2 py-2 text-start text-sm',
                        selectedRequest?.id === request.id && 'sm:bg-muted'
                      )}
                      onClick={() => handleSelectRequest(request)}
                    >
                      <div className="flex gap-3 w-full">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-sm">
                              {request.client?.name}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatRelativeTime(request.created_at)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs text-muted-foreground">
                              Re: Candidate-{candidateId}
                            </span>
                            {getUnreadBadge(request.id)}
                          </div>
                          <p className="text-xs text-muted-foreground group-hover:text-accent-foreground/90 line-clamp-2 text-ellipsis">
                            {previewText}
                          </p>
                        </div>
                      </div>
                    </button>
                    <Separator className="my-1" />
                  </Fragment>
                );
              })
            )}
          </ScrollArea>
        </div>

        {/* Right Panel - Message Thread */}
        {selectedRequest ? (
          <div
            className={cn(
              'bg-background absolute inset-0 start-full z-50 hidden w-full flex-1 flex-col border shadow-xs sm:static sm:z-auto sm:flex sm:rounded-md',
              mobileSelectedRequest && 'start-0 flex'
            )}
          >
            {/* Header */}
            <div className="bg-card mb-1 flex flex-none justify-between p-4 shadow-lg sm:rounded-t-md">
              <div className="flex gap-3">
                <Button
                  size="icon"
                  variant="ghost"
                  className="-ms-2 h-full sm:hidden"
                  onClick={() => setMobileSelectedRequest(null)}
                >
                  <ArrowLeft className="rtl:rotate-180" />
                </Button>
                <div className="flex items-center gap-2 lg:gap-4">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <span className="col-start-2 row-span-2 text-sm font-medium lg:text-base">
                      {selectedRequest.client?.name}
                    </span>
                    <span className="text-muted-foreground col-start-2 row-span-2 row-start-2 line-clamp-1 block text-xs text-nowrap text-ellipsis lg:text-sm">
                      Re: Candidate-{generateCandidateDisplayId(selectedRequest.candidate?.id)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Chat Messages */}
            <div className="flex flex-1 flex-col gap-2 rounded-md px-4 pt-0 pb-4">
              <div className="flex size-full flex-1">
                <div className="chat-text-container relative -me-4 flex flex-1 flex-col overflow-y-hidden">
                  {messagesLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  ) : (
                    <div className="chat-flex flex h-40 w-full grow flex-col justify-start gap-4 overflow-y-auto py-2 pe-4 pb-4">
                      {messages.length === 0 && !selectedRequest?.message ? (
                        <div className="text-center text-sm text-muted-foreground">
                          No messages yet
                        </div>
                      ) : (
                        <>
                          {/* Display initial request message if it exists */}
                          {selectedRequest?.message && (
                            <Fragment key="initial-message">
                              <div className="text-center text-xs text-muted-foreground">
                                {new Date(selectedRequest.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </div>
                              <div className="flex justify-start">
                                <div className="flex gap-2 max-w-[80%]">
                                  <Avatar className="size-8 shrink-0">
                                    <AvatarFallback className="bg-primary/10 text-primary">
                                      {selectedRequest.client?.name?.charAt(0) || 'C'}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div className="flex flex-col gap-1">
                                    <div className="bg-muted rounded-lg px-3 py-2">
                                      <p className="text-sm whitespace-pre-wrap break-words">
                                        {selectedRequest.message}
                                      </p>
                                    </div>
                                    <span className="text-xs text-muted-foreground">
                                      {formatTimestamp(selectedRequest.created_at)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </Fragment>
                          )}

                          {/* Display conversation messages */}
                          {messages.map((msg, index) => {
                            const isRecruiter = msg.sender_type === 'recruiter';
                            const showDate = index === 0 ||
                              new Date(messages[index - 1].created_at).toDateString() !== new Date(msg.created_at).toDateString();

                            return (
                              <Fragment key={msg.id}>
                                {showDate && (
                                  <div className="text-center text-xs text-muted-foreground">
                                    {new Date(msg.created_at).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </div>
                                )}

                                <div
                                  className={cn(
                                    'max-w-[85%] px-4 py-3 shadow-lg',
                                    isRecruiter
                                      ? 'bg-primary/90 text-primary-foreground/90 self-end rounded-[16px_16px_0_16px]'
                                      : 'bg-muted self-start rounded-[16px_16px_16px_0]'
                                  )}
                                >
                                  <div className={cn(
                                    'flex items-center gap-2 mb-2',
                                    isRecruiter ? 'justify-end' : 'justify-start'
                                  )}>
                                    {!isRecruiter && (
                                      <Avatar className="h-6 w-6">
                                        <AvatarFallback className="bg-blue-100 text-blue-700 text-xs">
                                          {msg.sender_name?.charAt(0) || 'C'}
                                        </AvatarFallback>
                                      </Avatar>
                                    )}
                                    <div className={cn('text-xs', isRecruiter ? 'text-right' : 'text-left')}>
                                      <span className="font-semibold block">
                                        {isRecruiter ? 'You' : msg.sender_name}
                                      </span>
                                      {!isRecruiter && msg.sender_email && (
                                        <span className="block text-muted-foreground">
                                          {msg.sender_email}
                                        </span>
                                      )}
                                    </div>
                                    {isRecruiter && (
                                      <Avatar className="h-6 w-6">
                                        <AvatarFallback className="bg-primary-foreground/20 text-primary-foreground text-xs">
                                          R
                                        </AvatarFallback>
                                      </Avatar>
                                    )}
                                  </div>
                                  <p className="text-sm whitespace-pre-wrap break-words">
                                    {msg.message}
                                  </p>
                                  <span
                                    className={cn(
                                      'mt-2 block text-xs font-light italic',
                                      isRecruiter
                                        ? 'text-primary-foreground/85 text-end'
                                        : 'text-foreground/75'
                                    )}
                                  >
                                    {formatTimestamp(msg.created_at)}
                                  </span>
                                </div>
                              </Fragment>
                            );
                          })
                          }
                        </>
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>
              </div>

              {/* Message Input (only show if not closed) */}
              {selectedRequest.status !== 'closed' && (
                <div className="space-y-2 mt-2">
                  {sendError && (
                    <Alert variant="destructive" className="py-2">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription className="text-xs">{sendError}</AlertDescription>
                    </Alert>
                  )}

                  <div className="border-input bg-card focus-within:ring-ring flex flex-col gap-2 rounded-md border p-3 focus-within:ring-1 focus-within:outline-hidden">
                    <Label htmlFor="message-input" className="text-sm font-medium">
                      Send a message
                    </Label>
                    <textarea
                      id="message-input"
                      placeholder="Type your message..."
                      className="min-h-[80px] w-full resize-none bg-inherit text-sm focus-visible:outline-hidden"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      disabled={sending}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                    />
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">
                        Press Enter to send, Shift+Enter for new line
                      </span>
                      <Button
                        onClick={handleSendMessage}
                        disabled={sending || !newMessage.trim()}
                        size="sm"
                      >
                        {sending ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="mr-2 h-4 w-4" />
                            Send
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Info Panel */}
              <Card className="mt-2">
                <CardContent className="p-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Client:</span>
                    <span className="font-medium">{selectedRequest.client?.name}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Candidate:</span>
                    <span className="font-medium">
                      {selectedRequest.candidate?.first_name} {selectedRequest.candidate?.last_name}
                      <span className="text-muted-foreground ml-1">
                        ({generateCandidateDisplayId(selectedRequest.candidate?.id)})
                      </span>
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          // Empty State
          <div
            className={cn(
              'bg-card absolute inset-0 start-full z-50 hidden w-full flex-1 flex-col justify-center rounded-md border shadow-xs sm:static sm:z-auto sm:flex'
            )}
          >
            <div className="flex flex-col items-center space-y-6 p-8">
              <div className="border-border flex size-16 items-center justify-center rounded-full border-2">
                <MessagesSquare className="size-8" />
              </div>
              <div className="space-y-2 text-center">
                <h1 className="text-xl font-semibold">Client Messages</h1>
                <p className="text-muted-foreground text-sm max-w-sm">
                  Select a message from the list to view the conversation and respond to the client.
                </p>
              </div>
            </div>
          </div>
        )}
      </section>
      </TabsContent>
    </Tabs>
  );
};

export default CandidateRequests;
